import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useTelaEstreita } from '../utils/telaEstreita';

/**
 * O botão voltar do celular fecha o que está aberto — e só depois pergunta.
 *
 * ── O problema ────────────────────────────────────────────────────────────
 *
 * O app é uma tela só. No celular, apertar voltar por mania saía do app, e o
 * que se perdia era o formulário pela metade, o filtro escolhido, o mês
 * navegado e a posição da rolagem.
 *
 * ── O que NÃO dá para fazer ──────────────────────────────────────────────
 *
 * Bloquear o voltar. Não existe API para isso, e `beforeunload` não serve:
 * ele não dispara de forma confiável em navegação de histórico, não aceita
 * texto próprio, e o navegador decide sozinho se mostra alguma coisa.
 *
 * O que existe é a ARMADILHA DE HISTÓRICO: empurrar uma entrada a mais e
 * escutar o `popstate`. Quando ela volta, cai nessa entrada em vez de sair, e
 * a gente reempurra. É o que todo app web faz, e tem duas frestas honestas:
 * dois toques muito rápidos escapam, e fechar a aba não passa por aqui.
 *
 * ── Por que fechar antes de perguntar ────────────────────────────────────
 *
 * "Não tem nada pra voltar" era o diagnóstico, e não era bem isso: tem o chat,
 * os modais, os menus e o mês navegado. Nenhum respondia ao voltar, e é isso
 * que fazia o botão parecer morto.
 *
 * Com a pilha, a maior parte dos toques por mania passa a FAZER algo — que é
 * como um app nativo se comporta. A pergunta fica para quando não há mesmo
 * nada, e assim ela é rara. Se fosse só a pergunta, apertar por mania viraria
 * ver um modal por mania.
 *
 * ── Só no celular ────────────────────────────────────────────────────────
 *
 * No computador o voltar é uma navegação que a pessoa faz de propósito, e o
 * `Esc` já fecha os modais. Reter alguém lá seria atrito inventado.
 */

type Fechavel = { fechar: () => void };

interface Ctx {
  registrar: (f: Fechavel) => () => void;
}

const Contexto = createContext<Ctx | null>(null);

/**
 * Enquanto `aberto`, o voltar fecha isto em vez de sair do app.
 *
 * A ordem é a de montagem, e fecha do último para o primeiro — o que estiver
 * por cima é o que sai primeiro, como a pessoa espera.
 */
export const useFechavelComVoltar = (aberto: boolean, fechar: () => void): void => {
  const ctx = useContext(Contexto);
  /* Guarda a função mais recente sem religar o registro a cada render — senão
     a ordem da pilha se embaralharia a cada mudança de estado do componente. */
  const atual = useRef(fechar);
  atual.current = fechar;

  useEffect(() => {
    if (!ctx || !aberto) return;
    return ctx.registrar({ fechar: () => atual.current() });
  }, [ctx, aberto]);
};

/**
 * `confirmar` vem de FORA, e não de um `useConfirmar()` aqui dentro.
 *
 * A caixa de confirmação precisa se registrar nesta pilha (voltar cancela a
 * confirmação), então ela importa deste arquivo. Se este importasse de lá, os
 * dois ficariam num ciclo — que o empacotador até resolve, mas que passa a
 * depender da ordem em que ele monta os módulos. Receber por propriedade
 * deixa a seta apontando para um lado só.
 */
export const ProvedorDeVoltar: React.FC<{
  confirmar: (p: { titulo: string; texto?: React.ReactNode; acao?: string }) => Promise<boolean>;
  children: React.ReactNode;
}> = ({ confirmar, children }) => {
  const estreita = useTelaEstreita();

  const pilha = useRef<Fechavel[]>([]);
  const saindo = useRef(false);
  const perguntando = useRef(false);

  const registrar = useCallback((f: Fechavel) => {
    pilha.current.push(f);
    return () => { pilha.current = pilha.current.filter(x => x !== f); };
  }, []);

  useEffect(() => {
    if (!estreita) return;

    const armar = () => window.history.pushState({ fin: true }, '');
    armar();

    const aoVoltar = async () => {
      if (saindo.current) return;

      const topo = pilha.current[pilha.current.length - 1];
      /*
       * Reempurra ANTES de fechar ou perguntar. A entrada precisa existir de
       * novo já no próximo toque, e fechar um modal pode levar um quadro.
       */
      armar();

      if (topo) { topo.fechar(); return; }

      /* Duas perguntas empilhadas se ela martelar o botão. */
      if (perguntando.current) return;
      perguntando.current = true;
      const querSair = await confirmar({
        titulo: 'Sair do FinTracking?',
        texto: 'Você está numa tela só, não há para onde voltar dentro do app. Sair fecha o que estiver aberto.',
        acao: 'Sair',
      });
      perguntando.current = false;

      if (querSair) {
        saindo.current = true;
        /*
         * -2: uma para desfazer a entrada que acabamos de reempurrar, outra
         * para sair de verdade. Se o app foi aberto numa aba nova não há para
         * onde ir, e o navegador simplesmente não sai — nenhum navegador deixa
         * uma página fechar a aba por histórico.
         */
        window.history.go(-2);
      }
    };

    window.addEventListener('popstate', aoVoltar);
    return () => window.removeEventListener('popstate', aoVoltar);
  }, [estreita, confirmar]);

  return <Contexto.Provider value={{ registrar }}>{children}</Contexto.Provider>;
};
